import React, { useEffect, useState } from "react";
import {
  Form,
  Row,
  Input,
  Col,
  Table,
  Spin,
  Button,
  Modal,
  type TableProps,
  DatePicker,
  Space,
} from "antd";
import dayjs from 'dayjs';

import { CheckOutlined, ExclamationCircleOutlined } from "@ant-design/icons";

import { maskCnpj } from "../../../utils/maskFunctions";

import { useLocation, useNavigate } from "react-router-dom";
import { errorMessage, infoMessage, successMessage } from "../../../messages/messages";
import type { IAnularOperacaoRequisicao, IAutorizacaoEfpc, IPessoaJuridica } from "../../../components/Interfaces";

import { disabledDate, isStateValid, pushToBase } from "../../../utils/utilsFunctions";
import { PatternFormat } from "react-number-format";
import { apiPrimarias } from "../../../services/api";
import { PageHeader } from "@ant-design/pro-components";
import TextArea from "antd/es/input/TextArea";

const Operacoes: React.FC<any> = () => {

  const navigate = useNavigate();
  const { state } = useLocation();

  const [form] = Form.useForm();

  const { confirm } = Modal;

  const [operacoesAutorizarEfpc, setOperacoesAutorizarEfpc] = useState<IAutorizacaoEfpc[]>([]);

  const [loading, setLoading] = useState(false);
  const [tipLoading, _setTipLoading] = useState("");

  const [pessoaJuridicaRequerente, setPessoaJuridicaRequerente] = useState<IPessoaJuridica>();
  const [pessoaJuridicaEfpc, setPessoaJuridicaEfpc] = useState<IPessoaJuridica>();
  const [operacaoSelecionada, setOperacaoSelecionada] = useState<IAutorizacaoEfpc>();

  const [operacaoSelectedRowKeys, setOperacaoSelectedRowKeys] = useState<React.Key[]>([]);
  const [modalRequisicaoAberto, setModalRequisicaoAberto] = useState(false);
  const [modalAnulacaoAberto, setModalAnulacaoAberto] = useState(false);
  const [tipoAcao, setTipoAcao] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0)

    if (isStateValid(state)) {
      //console.log(state);
      validarPessoaJuridicaRequerente(state.data);
    } else {
      errorMessage("Não foi possível identificar as operações de autorização da Entidade.");
      pushToBase(navigate);
    }
  }, []);

  useEffect(() => {
    if (pessoaJuridicaRequerente) {
      if (pessoaJuridicaRequerente?.serie) getOperacaoPorSeriePessoaJuridica(pessoaJuridicaRequerente?.serie);
      form.setFieldsValue({
        operacaoRazaoSocial: pessoaJuridicaRequerente.razaoSocial,
        operacaoCnpjRequerente: pessoaJuridicaRequerente.numero
      });
    }
  }, [pessoaJuridicaRequerente]);

  useEffect(() => {
    if (operacaoSelecionada) {
      setPessoaJuridicaEfpc(operacaoSelecionada.pessoaJuridicaObjeto);
    }
  }, [operacaoSelecionada]);

  useEffect(() => {
    if (tipoAcao > 0) {
      mostrarConfirmar(tipoAcao);
    }
  }, [tipoAcao]);

  function validarPessoaJuridicaRequerente(pjReq: any) {
    if (!pjReq || pjReq.length === 0) {
      errorMessage("Não foi possível identificar a Pessoa Jurídia do Requerente");
      pushToBase(navigate);
    } else {
      setPessoaJuridicaRequerente(pjReq);
    }
  }

  const getOperacaoPorSeriePessoaJuridica = async (serie: number) => {
    try {
      setLoading(true);

      let lQuery = `/efpc/autorizacao/${serie}/serie/todas`;
      const { data } = await apiPrimarias.get(lQuery);
      //console.log(data);
      setOperacoesAutorizarEfpc(data);
      setOperacaoSelecionada(undefined);
    } catch (error) {
      //console.log(error);
      errorMessage("Não foi possível realizar a consulta");
    } finally {
      setLoading(false);
    }
  };

  function pushToEfpcCadastrar() {
    let tpSelecao = {
      data: operacaoSelecionada,
      dataPessoaJuridicaRequerente: pessoaJuridicaRequerente,
      dataPessoaJuridicaEfpc: pessoaJuridicaEfpc
    };

    navigate("/" + import.meta.env.VITE_APP_BASE + "/efpc-cadastrar", { state: tpSelecao });
  }

  function pushToNovaOperacaoAutorizarEfpc() {
    let tpSelecao = {
      data: pessoaJuridicaRequerente,
    };

    navigate("/" + import.meta.env.VITE_APP_BASE + "/pessoa-juridica-operacao-autorizacao-efpc", { state: tpSelecao });
  }

  function gerarColunasOperacoes() {
    let cols: any[] = [];

    cols.push({
      // key: "cnpj",
      width: 150,
      title: "CNPJ Requerente",
      dataIndex: "",
      render: (aut: IAutorizacaoEfpc) => (
        <Input
          value={aut.numeroPessoaJuridicaRequerente ? maskCnpj(aut.numeroPessoaJuridicaRequerente) : undefined}
          size={"small"}
          style={{ padding: 0 }}
          variant="borderless"
        />
      ),
    });

    cols.push({
      // key: "cnpj",
      width: 150,
      title: "Número",
      dataIndex: "numero",
    });

    cols.push({
      // key: "cnpj",
      width: 150,
      title: "Nome",
      dataIndex: "nmOperacao",
    });

    cols.push({
      width: 150,
      title: "Situação",
      dataIndex: "",
      render: (aut: IAutorizacaoEfpc) => (
        <>
          {aut.situacao && aut.situacao === 3 ? "Anulada" : aut.situacao === 2 ? "Cancelada" : aut.situacao === 1 ? "Aprovada" : ""}
        </>
      ),
    });

    cols.push({
      title: "Objeto da Operação",
      dataIndex: "",
      render: (aut: IAutorizacaoEfpc) => (
        <>
          {aut.pessoaJuridicaObjeto?.razaoSocial}
        </>
      ),
    });

    cols.push({
      width: 150,
      title: "Data Posição",
      dataIndex: "",
      render: (aut: IAutorizacaoEfpc) => (
        <>
          {aut.dataPosicao && aut.dataPosicao !== "" ? dayjs(aut.dataPosicao).format("DD/MM/YYYY") : ""}
        </>
      ),
    });

    return cols.filter(Boolean);
  }

  const columnsOperacoes = gerarColunasOperacoes();

  function mostrarConfirmar(tipo: number) {

    confirm({
      title: tipo === 1 ? "Deseja Aprovar a Operação e Efetuar o Cadastro da EFPC?" : tipo === 2 ? "Deseja Cancelar a Operação?" : "Deseja Anular a Operação?",
      width: "700px",
      icon: <ExclamationCircleOutlined />,
      content: "Pessoa Jurídica EFPC: " + pessoaJuridicaEfpc?.razaoSocial + ". Operação: " + operacaoSelecionada?.numero,
      okButtonProps: { id: 'ok' },
      onOk() {
        form.validateFields();
        window.scrollTo(0, 0)
        form.submit();
      },
      onCancel() { setTipoAcao(0) },
    });

  }

  const operacaoRowSelection: TableProps<IAutorizacaoEfpc>['rowSelection'] = {
    type: "radio", // ensures only one row selected
    selectedRowKeys: operacaoSelectedRowKeys,
    onChange: (newSelectedKeys, selectedRows: IAutorizacaoEfpc[]) => {
      if (selectedRows[0].situacao === 3) {
        infoMessage("Operação já anulada");
      } else if (selectedRows[0].situacao === 2) {
        infoMessage("Operação já cancelada");
      } else {
        setOperacaoSelectedRowKeys(newSelectedKeys);
        setOperacaoSelecionada(selectedRows[0]); // store selected object
      }
    },
  };


  const efpcAutorizacaoRequerer = async (numero: string) => {
    setLoading(true);
    const currentForm = form.getFieldsValue();

    const jsonRequisicao: IAutorizacaoEfpc =
    {
      numeroPessoaJuridicaRequerente: numero,
      dataPosicao: currentForm.dataPosicao ? dayjs(currentForm.dataPosicao).format('YYYY-MM-DDTHH:mm:ss') : "",
      justificativa: currentForm.justificativaAutorizacao,
    }

    //console.log("json subindo");
    //console.log(jsonRequisicao);

    try {
      let query = `/efpc/autorizacao/requerer`;

      const { data } = await apiPrimarias.post(query, jsonRequisicao);
      setLoading(false);

      //console.log(data);

    } catch (error: any) {
      setLoading(false);
      if (error.response) {
        errorMessage(`${error.response.data.mensagem}`);
        if (error.response.data.campos) {
          error.response.data.campos.map((c: any) => {
            errorMessage(`${c.cmensagem}`);
          });
        }
      }
      // Return comentado pq esta retornando essa mensagem de erro por default para todos os erros de cadastro
      // return errorMessage("Ocorreu um erro ao tentar realizar uma operação");
    } finally {
      setModalRequisicaoAberto(false);
    }
  };


  const cancelarOperacao = async (numero: string) => {

    if (numero === "") return;

    setLoading(true);

    try {
      let query = `/efpc/autorizacao/cancelar/${numero}`;

      await apiPrimarias.post(query);
      setLoading(false);

      successMessage("Operação Cancelada com sucesso.");
      getOperacaoPorSeriePessoaJuridica(pessoaJuridicaRequerente?.serie || 0);
      //console.log(data);

    } catch (error: any) {
      setLoading(false);
      if (error.response) {
        errorMessage(`${error.response.data.mensagem}`);
        if (error.response.data.campos) {
          error.response.data.campos.map((c: any) => {
            errorMessage(`${c.cmensagem}`);
          });
        }
      }
      // Return comentado pq esta retornando essa mensagem de erro por default para todos os erros de cadastro
      // return errorMessage("Ocorreu um erro ao tentar realizar uma operação");
    } finally {
      setModalRequisicaoAberto(false);
    }
  };


  const anularOperacao = async (numero: string) => {
    setLoading(true);
    const currentForm = form.getFieldsValue();

    const json: IAnularOperacaoRequisicao =
    {
      justificativa: currentForm.justificativaAnulacao,
    }

    try {
      let query = `/efpc/autorizacao/anular/${numero}`;

      await apiPrimarias.post(query, json);
      setLoading(false);

      successMessage("Operação Anulada com sucesso.");
      getOperacaoPorSeriePessoaJuridica(pessoaJuridicaRequerente?.serie || 0);

    } catch (error: any) {
      setLoading(false);
      if (error.response) {
        errorMessage(`${error.response.data.mensagem}`);
        if (error.response.data.campos) {
          error.response.data.campos.map((c: any) => {
            errorMessage(`${c.cmensagem}`);
          });
        }
      }
      // Return comentado pq esta retornando essa mensagem de erro por default para todos os erros de cadastro
      // return errorMessage("Ocorreu um erro ao tentar realizar uma operação");
    } finally {
      setModalRequisicaoAberto(false);
    }
  };

  function identificarAcao() {
    if (tipoAcao === 1) {
      pushToEfpcCadastrar();
    } else if (tipoAcao === 2) {
      cancelarOperacao(operacaoSelecionada?.numero || "");
    } else {
      anularOperacao(operacaoSelecionada?.numero || "");
    }
  }


  return (
    <Spin spinning={loading} tip={tipLoading}>
      <Modal title="Requisição de Operação de Autorização de Entidade por Pessoa Jurídica"
        open={modalRequisicaoAberto}
        onOk={() => {
          if (pessoaJuridicaRequerente?.numero)
            efpcAutorizacaoRequerer(pessoaJuridicaRequerente.numero)
          else
            errorMessage("Número (CNPJ, NIF, Processo) da Pessoa Jurídica não localizada.");

        }}
        onCancel={() => setModalRequisicaoAberto(false)}
      >
        <Form layout="vertical" form={form}>
          <p>Requisitante: {pessoaJuridicaRequerente?.razaoSocial} </p>

          <Row>
            <Col xs={24} md={12} xl={6}>
              <Form.Item
                label="Data Posição"
                name="dataPosicao"
                rules={[
                  {
                    required: true,
                    message: "Data posição não preenchido",
                  },
                ]}
              >
                <DatePicker
                  format={"DD/MM/YYYY"}
                  style={{ width: "100%" }}
                  disabledDate={disabledDate}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col xs={24} md={24} xl={24}>
              <Form.Item
                label="Justificativa para a Autorização"
                name="justificativaAutorizacao"
                rules={[
                  {
                    required: true,
                    message: "Preencha a justificativa",
                  },
                ]}
              >
                <TextArea />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal title="Anulação de Operação"
        open={modalAnulacaoAberto}
        onOk={() => {
          if (pessoaJuridicaRequerente?.numero) {
            setTipoAcao(3);
            setModalAnulacaoAberto(false);
          }
          else
            errorMessage("Número (CNPJ, NIF, Processo) da Pessoa Jurídica não localizada.");

        }}
        onCancel={() => setModalAnulacaoAberto(false)}
      >
        <Form layout="vertical" form={form}>
          <p>Requisitante: {pessoaJuridicaRequerente?.razaoSocial} </p>

          <Row>
            <Col xs={24} md={24} xl={24}>
              <Form.Item
                label="Justificativa para a Anulação"
                name="justificativaAnulacao"
                rules={[
                  {
                    required: true,
                    message: "Informe a justificativa",
                  },
                ]}
              >
                <TextArea />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Form layout="vertical" form={form} onFinish={identificarAcao}>
        <div className="main-content pl-sm-3 mt-4">
          <h1>Operações por Pessoa Jurídica</h1>
        </div>

        <div className="main-content pl-sm-3 mt-4">
          <h2>Informações do Requerente</h2>
        </div>

        <Row>
          <Col xs={24} xl={6}>
            <Form.Item
              label="CNPJ do Requerente"
              name="operacaoCnpjRequerente">
              <PatternFormat
                disabled
                customInput={Input}
                format="##.###.###/####-##"
              />
            </Form.Item>
          </Col>

          <Col xs={24} xl={12}>
            <Form.Item label="Razão Social"
              name="operacaoRazaoSocial">
              <Input disabled={true} />
            </Form.Item>
          </Col>
        </Row>

        <div className="main-content pl-sm-3 mt-4">
          <h2>Novas Operações</h2>
        </div>

        <Row>
          {
            <>
              <Button shape='round'
                type='primary' icon={<CheckOutlined />} htmlType='button'
                onClick={() => {
                  pushToNovaOperacaoAutorizarEfpc();
                }} >
                Autorização de Entidade
              </Button>
            </>
          }
        </Row>

        <div className="main-content pl-sm-3 mt-4">
          <h2>Operações vinculadas à Pessoa Jurídica</h2>
        </div>
        <Table
          // className="overflowConsulta"
          rowSelection={operacaoRowSelection}
          dataSource={operacoesAutorizarEfpc}
          size="middle"
          columns={columnsOperacoes}
          rowKey="numero"
          loading={loading}
          scroll={{ x: "max-content", y: 350 }}
        />

        <PageHeader
          className='site-page-header'
          style={{ background: "#FFF" }}
          extra={
            <div
              style={{
                display: "flex",
                width: "max-content",
                justifyContent: "flex-end",
              }}
            >
              <Space align='center'>

                <Button shape='round'
                  disabled={!operacaoSelecionada || operacaoSelecionada.situacao !== 0 || !pessoaJuridicaEfpc}
                  type='primary' icon={<CheckOutlined />} htmlType='button'
                  onClick={() => {
                    setTipoAcao(1);
                  }} >
                  Aprovar Operação
                </Button>

                <Button shape='round'
                  disabled={!operacaoSelecionada || operacaoSelecionada.situacao !== 0 || !pessoaJuridicaEfpc}
                  type='primary' icon={<CheckOutlined />} htmlType='button'
                  onClick={() => {
                    setTipoAcao(2);
                  }} >
                  Cancelar Operação
                </Button>

                <Button shape='round'
                  disabled={!operacaoSelecionada || operacaoSelecionada.situacao !== 1 || !pessoaJuridicaEfpc}
                  type='primary' icon={<CheckOutlined />} htmlType='button'
                  onClick={() => {
                    setModalAnulacaoAberto(true);
                  }} >
                  Anular Operação
                </Button>


              </Space>
            </div>
          }
        />


      </Form>

    </Spin >
  );
};

export default Operacoes;
